import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Car, CreditCard, Buildings, ChatCircleText, Briefcase, Bell, CalendarCheck, Warning, HandHeart, DownloadSimple, UsersThree, Clock, MapTrifold, CursorClick } from "@phosphor-icons/react";

const priority = [
  { key: "total_new", label: "New Requests", to: "/admin/ride-requests", icon: Bell, tone: "from-brand-purple to-brand-purple2", testid: "priority-new" },
  { key: "due_today", label: "Follow-Ups Due Today", to: "/admin/follow-ups", icon: CalendarCheck, tone: "from-brand-teal to-brand-teal2", testid: "priority-due-today" },
  { key: "overdue", label: "Overdue Follow-Ups", to: "/admin/follow-ups", icon: Warning, tone: "from-rose-600 to-rose-500", testid: "priority-overdue" },
];

const cards = [
  { key: "facility-leads", label: "Facility Partnership Leads", to: "/admin/facility-leads", icon: Buildings, color: "text-brand-teal bg-brand-teal/10" },
  { key: "odp-referrals", label: "ODP Referrals", to: "/admin/odp-referrals", icon: HandHeart, color: "text-brand-purple bg-brand-purple/10" },
  { key: "ride-requests", label: "Ride Requests", to: "/admin/ride-requests", icon: Car, color: "text-brand-blue bg-brand-blue/10" },
  { key: "quotes", label: "Private Pay Quotes", to: "/admin/quotes", icon: CreditCard, color: "text-brand-purple bg-brand-purple/10" },
  { key: "applications", label: "Job Applications", to: "/admin/applications", icon: Briefcase, color: "text-rose-600 bg-rose-100" },
  { key: "contacts", label: "Contact Messages", to: "/admin/contacts", icon: ChatCircleText, color: "text-amber-600 bg-amber-100" },
];

export default function Overview() {
  const [stats, setStats] = useState({});
  const [rec, setRec] = useState(null);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/recruiting-stats").then((r) => setRec(r.data)).catch(() => {});
  }, []);
  const fu = stats.follow_ups || {};
  const priorityVal = (k) => (k === "total_new" ? stats.total_new : fu[k]) || 0;

  const exportAll = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/export-all-leads", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `caredeserved-all-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("All leads exported");
    } catch { toast.error("Export failed"); } finally { setExporting(false); }
  };

  return (
    <div data-testid="admin-overview">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Dashboard Overview</h1>
          <p className="mt-1 text-sm text-slate-500">What needs your attention right now.</p>
        </div>
        <Button data-testid="export-all-leads" onClick={exportAll} disabled={exporting}
          className="rounded-full bg-brand-blue px-6 font-semibold text-white hover:bg-brand-blue2">
          <DownloadSimple size={18} weight="bold" className="mr-1.5" /> {exporting ? "Exporting…" : "Export All Leads"}
        </Button>
      </div>

      {/* Priority row */}
      <div className="mt-6 grid gap-5 sm:grid-cols-3">
        {priority.map((p) => (
          <Link key={p.key} to={p.to} data-testid={p.testid}
            className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${p.tone} p-6 text-white shadow-sm transition-transform hover:-translate-y-0.5`}>
            <div className="flex items-center justify-between">
              <p.icon size={30} weight="duotone" className="opacity-90" />
              <span className="font-heading text-4xl font-extrabold">{priorityVal(p.key)}</span>
            </div>
            <p className="mt-3 text-sm font-semibold uppercase tracking-wide opacity-95">{p.label}</p>
          </Link>
        ))}
      </div>

      {/* Lead type counts */}
      <h2 className="mt-10 font-heading text-lg font-bold text-slate-900">All Requests</h2>
      <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => {
          const s = stats[c.key] || {};
          return (
            <Link key={c.key} to={c.to} data-testid={`stat-${c.key}`}
              className="rounded-2xl border border-slate-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex items-center justify-between">
                <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${c.color}`}>
                  <c.icon size={26} weight="duotone" />
                </span>
                {s.new > 0 && <span className="rounded-full bg-brand-purple px-2.5 py-1 text-xs font-bold text-white">{s.new} new</span>}
              </div>
              <p className="mt-4 font-heading text-3xl font-bold text-slate-900">{s.total ?? 0}</p>
              <p className="text-sm text-slate-500">{c.label}</p>
            </Link>
          );
        })}
      </div>

      {/* Driver recruitment analytics */}
      {rec && (
        <div data-testid="recruiting-stats">
          <div className="mt-10 flex items-center gap-2">
            <h2 className="font-heading text-lg font-bold text-slate-900">Driver Recruitment — This Week</h2>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">Internal only</span>
          </div>
          <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { key: "total", label: "Applications This Week", value: rec.total, icon: Briefcase, color: "text-brand-blue bg-brand-blue/10", testid: "rec-total" },
              { key: "full_time", label: "Full-Time Applicants", value: rec.full_time, icon: UsersThree, color: "text-brand-teal bg-brand-teal/10", testid: "rec-fulltime" },
              { key: "part_time", label: "Part-Time Applicants", value: rec.part_time, icon: Clock, color: "text-brand-purple bg-brand-purple/10", testid: "rec-parttime" },
              { key: "new_awaiting", label: "New — Awaiting Review", value: rec.new_awaiting, icon: Bell, color: "text-rose-600 bg-rose-100", testid: "rec-new-awaiting" },
            ].map((t) => (
              <div key={t.key} data-testid={t.testid} className="rounded-2xl border border-slate-200 bg-white p-6">
                <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${t.color}`}>
                  <t.icon size={26} weight="duotone" />
                </span>
                <p className="mt-4 font-heading text-3xl font-bold text-slate-900">{t.value ?? 0}</p>
                <p className="text-sm text-slate-500">{t.label}</p>
              </div>
            ))}
          </div>

          <div data-testid="rec-by-county" className="mt-5 rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2">
              <MapTrifold size={20} weight="duotone" className="text-brand-purple" />
              <p className="font-heading text-base font-semibold text-slate-900">Applications by County</p>
            </div>
            <p className="mt-1 text-sm text-slate-500">Where applicants are coming from — helps show which county-aware campaigns are working.</p>
            {(() => {
              const items = Object.entries(rec.by_county || {})
                .filter(([, n]) => n > 0)
                .sort((a, b) => (a[0] === "Unspecified") - (b[0] === "Unspecified") || b[1] - a[1] || a[0].localeCompare(b[0]));
              if (items.length === 0) return <p className="mt-4 text-sm text-slate-400">No driver applications yet this week.</p>;
              const max = Math.max(...items.map(([, n]) => n));
              return (
                <ul className="mt-4 space-y-2.5">
                  {items.map(([county, n]) => (
                    <li key={county} className="flex items-center gap-3">
                      <span className="w-32 shrink-0 text-sm font-medium text-slate-700">{county === "Unspecified" ? "Unspecified" : `${county} County`}</span>
                      <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                        <span className="block h-full rounded-full bg-brand-purple" style={{ width: `${Math.max(8, (n / max) * 100)}%` }} />
                      </span>
                      <span className="w-8 shrink-0 text-right font-heading text-sm font-bold text-slate-900">{n}</span>
                    </li>
                  ))}
                </ul>
              );
            })()}
          </div>

          {rec.by_position && (
            <div data-testid="rec-by-position" className="mt-5 rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-2">
                <Briefcase size={20} weight="duotone" className="text-brand-teal" />
                <p className="font-heading text-base font-semibold text-slate-900">Applications by Position</p>
              </div>
              <p className="mt-1 text-sm text-slate-500">How many applicants each job opening has attracted.</p>
              {(() => {
                const items = Object.entries(rec.by_position || {})
                  .filter(([, n]) => n > 0)
                  .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
                if (items.length === 0) return <p className="mt-4 text-sm text-slate-400">No applications yet this week.</p>;
                const max = Math.max(...items.map(([, n]) => n));
                return (
                  <ul className="mt-4 space-y-2.5">
                    {items.map(([title, n]) => (
                      <li key={title} className="flex items-center gap-3">
                        <span className="w-48 shrink-0 truncate text-sm font-medium text-slate-700" title={title}>{title}</span>
                        <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                          <span className="block h-full rounded-full bg-brand-teal" style={{ width: `${Math.max(8, (n / max) * 100)}%` }} />
                        </span>
                        <span className="w-8 shrink-0 text-right font-heading text-sm font-bold text-slate-900">{n}</span>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
          )}

          {rec.ticker && (
            <div data-testid="rec-ticker" className="mt-5 grid gap-5 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal">
                  <CursorClick size={26} weight="duotone" />
                </span>
                <p className="mt-4 font-heading text-3xl font-bold text-slate-900">{rec.ticker.clicks_total ?? 0}</p>
                <p className="text-sm text-slate-500">Ticker Clicks (all time)</p>
                <p className="mt-1 text-xs font-medium text-brand-teal">+{rec.ticker.clicks_week ?? 0} this week</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-purple/10 text-brand-purple">
                  <Briefcase size={26} weight="duotone" />
                </span>
                <p className="mt-4 font-heading text-3xl font-bold text-slate-900">{rec.ticker.proceed_total ?? 0}</p>
                <p className="text-sm text-slate-500">Reached Careers Page from Ticker</p>
                <p className="mt-1 text-xs font-medium text-brand-purple">+{rec.ticker.proceed_week ?? 0} this week</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
